import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, computed, effect, inject, OnInit, PLATFORM_ID } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterOutlet } from '@angular/router';
import {
  AuthService,
  AuthState,
  CacheService,
  CheckoutData,
  DialogService,
  EkycNavigationService,
  environment,
  GoogleTagService,
  PaymentWebSocketService,
  RewardPointsService,
  SimsService,
  StorageService,
  SubscriberService,
  WebsocketService,
} from '@gb-webapp/gb-app';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { AuthComponent } from './pages/auth/auth.component';
import { EsimSelectionDialogComponent } from './pages/buy-plan/dialogs/esim-selection-dialog/esim-selection-dialog.component';

// Languages that are read from right to left.
export const RightToLeftLanguages: string[] = ['ar', 'he', 'fa', 'ur'];

// SessionStorage key for tracking referral code dialog dismissal.
const REFERRAL_CODE_DISMISSED_KEY = 'referralCodeDismissed';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, CommonModule, TranslateModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  public title = 'gb-webapp-yez';

  public headerTitle = '';

  protected platformId = inject(PLATFORM_ID);

  protected authService = inject(AuthService);

  protected subscriberService = inject(SubscriberService);

  protected simsService = inject(SimsService);

  protected paymentWebSocketService = inject(PaymentWebSocketService);

  protected websocketService = inject(WebsocketService);

  protected googleTagService = inject(GoogleTagService);

  protected router = inject(Router);

  protected rewardPointsService = inject(RewardPointsService);

  protected translateService = inject(TranslateService);

  protected cacheService = inject(CacheService);

  protected dialogService = inject(DialogService);

  protected ekycNavigationService = inject(EkycNavigationService);

  protected storageService = inject(StorageService);

  protected isAuthenticated = this.authService.isAuthenticated;

  isRewardPointsEnabled = computed(() => {
    return this.cacheService.cachedContent()?.cache.config.rewardPointsMasterEnabled;
  });

  protected currentTranslation = toSignal(
    this.translateService.onLangChange.pipe(map((result) => result.lang)),
    {
      initialValue: null,
    }
  );

  protected $document = inject(DOCUMENT);

  constructor() {
    effect(() => {
      const currentTranslation = this.currentTranslation();

      if (currentTranslation) {
        this.$document.documentElement.lang = currentTranslation;

        // Handle RTL/LTR text direction
        this.$document.body.classList.remove('text-end');
        if (RightToLeftLanguages.includes(currentTranslation)) {
          this.$document.body.classList.add('text-end');
        }
      }
    });

    effect(() => {
      if (isPlatformBrowser(this.platformId)) {
        const isAuthenticated = this.isAuthenticated();
        if (isAuthenticated) {
          // Initialize auth state from storage
          this.authService.initAuthSate();

          this.subscriberService.initAutoLoadSubscriber();
          this.simsService.initAutoLoadSims();
          this.rewardPointsService.initAutoLoadUserRewardPointsData();
        }
      }
    });

    effect(() => {
      const allowReferralCode = this.subscriberService.subscriber()?.allowReferralCode;
      if (allowReferralCode) {
        this.showReferralCode(allowReferralCode);
      }
    });

    if (isPlatformBrowser(this.platformId)) {
      console.log(`🚀 Version: ${environment.version}`);
      if (!environment.env.startsWith('prod-')) {
        console.log(`🌍 Environment: ${environment.env}, Preview Mode: ${environment.previewMode}`);
      }

      this.googleTagService.pushEvent('first_visit', {
        page: this.router.url,
      });
    }
  }

  public ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      // Payment events use the central WebsocketService. Socket.io payment WS disabled for now.
      // this.paymentWebSocketService.connect();
    }
  }

  public showReferralCode(allowReferralCode: boolean) {
    // Open referral code modal for first-time sign-in (CLIENT-SIDE ONLY)
    // Check if modal is already open to prevent duplicate dialogs
    // Check if user has already dismissed the dialog in this session
    const isDismissed = isPlatformBrowser(this.platformId)
      ? sessionStorage.getItem(REFERRAL_CODE_DISMISSED_KEY) === 'true'
      : false;

    if (
      this.isRewardPointsEnabled() &&
      allowReferralCode &&
      !isDismissed &&
      !this.dialogService.isModalOpen('referralCodeModal')
    ) {
      this.dialogService
        .openModal(AuthComponent, {
          props: {
            startUpState: AuthState.REFERRAL_CODE,
          },

          cssClass: 'modal-medium',
          modalId: 'referralCodeModal',
        })
        .subscribe(() => {
          // Mark as dismissed when modal is closed (user skipped or submitted)
          if (isPlatformBrowser(this.platformId)) {
            sessionStorage.setItem(REFERRAL_CODE_DISMISSED_KEY, 'true');
            const checkoutData = this.storageService.getCheckoutData();
            if (checkoutData) {
              const navigationContext: CheckoutData = {
                regionType: checkoutData?.regionType || '',
                destinationSlug: checkoutData?.destinationSlug || '',
                planSlug: checkoutData?.planSlug || '',
                imsiId: checkoutData?.imsiId || '',
                redirectUrl: checkoutData?.redirectUrl || '',
              };
              this.ekycNavigationService.shouldHandleButtonClick = true;
              this.ekycNavigationService.handleButtonClick(
                navigationContext,
                EsimSelectionDialogComponent,
                AuthComponent
              );
            }
          }
        });
    }
  }
}
